import React from 'react';

// Accepts currentTheme and isDarkTheme as props
export default function KnowledgeBasePage({ currentTheme, isDarkTheme }) {
  return (
    <div style={{
      backgroundColor: currentTheme.cardBg,
      border: `1px solid ${currentTheme.border}`,
      borderRadius: '8px',
      padding: '24px',
      color: currentTheme.textPrimary,
      fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
      boxShadow: isDarkTheme ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.07)'
    }}>
      <div style={{ 
        fontSize: '22px', 
        fontWeight: 700, 
        marginBottom: '20px', 
        color: currentTheme.textPrimary,
        borderBottom: `2px solid ${currentTheme.border}`,
        paddingBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'inherit'
      }}>
        📖 Technical Analysis Knowledge Base
      </div>

      <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
        <p style={{ marginBottom: '25px', color: currentTheme.textSecondary, fontSize: '18px' }}>
          🎓 <strong>Complete guide to technical indicators for algorithmic trading students</strong>
        </p>

        {/* Table of Contents */}
        <div style={{ 
          backgroundColor: isDarkTheme ? '#2a2f3f' : '#e8f0fe',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #4285f4',
          marginBottom: '30px'
        }}>
          <h3 style={{ color: '#4285f4', fontSize: '18px', marginBottom: '15px' }}>📚 Table of Contents</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div>• Moving Averages</div>
            <div>• Momentum Oscillators</div>
            <div>• Volume Indicators</div>
            <div>• Volatility Indicators</div>
            <div>• Trend Indicators</div>
            <div>• Channel Indicators</div>
          </div>
        </div>

        {/* Moving Averages Section */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            color: '#34a853', 
            fontSize: '22px', 
            marginBottom: '20px',
            borderLeft: '4px solid #34a853',
            paddingLeft: '15px'
          }}>
            📈 Moving Averages - The Foundation of Technical Analysis
          </h2>

          {/* SMA */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#2a3a2a' : '#f0f8f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #34a853',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#34a853', fontSize: '18px', marginBottom: '15px' }}>
              SMA - Simple Moving Average
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Invented in the early 1900s, SMA is the most basic trend-following indicator. 
                It was first used by grain traders to smooth out price fluctuations.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🧮 Calculation:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                SMA = (Price₁ + Price₂ + ... + Priceₙ) / n
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li>Trend identification: Price above SMA = uptrend, below = downtrend</li>
                <li>Support/Resistance: SMA acts as dynamic support in uptrends</li>
                <li>Crossover signals: When fast SMA crosses above slow SMA</li>
              </ul>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>⏰ When to Use:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Best in trending markets. Use SMA(20) for short-term, SMA(50) for medium-term, 
                and SMA(200) for long-term trends.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🎯 Where to Use:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                All timeframes and markets. Particularly effective in forex and stock indices.
              </p>
            </div>

            <div>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Example Strategy:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Entry: CLOSE &gt; SMA(20) AND SMA(20) &gt; SMA(50)<br/>
                Exit: CLOSE &lt; SMA(20)
              </div>
            </div>
          </div>

          {/* EMA */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#2a3a2a' : '#f0f8f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #34a853',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#34a853', fontSize: '18px', marginBottom: '15px' }}>
              EMA - Exponential Moving Average
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Developed in the 1960s to give more weight to recent prices. 
                Popularized by Gerald Appel in MACD calculations.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🧮 Calculation:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                EMA = Price × (2/(n+1)) + Previous EMA × (1-(2/(n+1)))
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li>More responsive than SMA - catches trend changes faster</li>
                <li>Better for short-term trading and volatile markets</li>
                <li>Used in MACD and other advanced indicators</li>
              </ul>
            </div>

            <div>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Example Strategy:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Entry: EMA(12) &gt; EMA(26) AND RSI(14) &gt; 50<br/>
                Exit: EMA(12) &lt; EMA(26)
              </div>
            </div>
          </div>
        </div>

        {/* Momentum Oscillators Section */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            color: '#ea4335', 
            fontSize: '22px', 
            marginBottom: '20px',
            borderLeft: '4px solid #ea4335',
            paddingLeft: '15px'
          }}>
            🌊 Momentum Oscillators - Measuring Price Velocity
          </h2>

          {/* RSI */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a2a2a' : '#fff0f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #ea4335',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#ea4335', fontSize: '18px', marginBottom: '15px' }}>
              RSI - Relative Strength Index
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Created by J. Welles Wilder in 1978. One of the most popular oscillators, 
                featured in his book "New Concepts in Technical Trading Systems".
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🧮 Calculation:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                RSI = 100 - (100 / (1 + RS))<br/>
                RS = Average Gain / Average Loss (over 14 periods)
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li>Overbought: RSI &gt; 70 (sell signal)</li>
                <li>Oversold: RSI &lt; 30 (buy signal)</li>
                <li>Divergence: Price makes new high but RSI doesn't</li>
                <li>Centerline crossover: RSI crossing above/below 50</li>
              </ul>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>⚠️ Student Tips:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Don't rely solely on overbought/oversold levels in strong trends. 
                RSI can stay "overbought" for extended periods during bull markets.
              </p>
            </div>

            <div>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Example Strategy:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Entry: RSI(14) &lt; 30 AND CLOSE &gt; SMA(50)<br/>
                Exit: RSI(14) &gt; 70
              </div>
            </div>
          </div>

          {/* MACD */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a2a2a' : '#fff0f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #ea4335',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#ea4335', fontSize: '18px', marginBottom: '15px' }}>
              MACD - Moving Average Convergence Divergence
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Developed by Gerald Appel in the late 1970s. Combines trend-following 
                and momentum characteristics in one indicator.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🧮 Calculation:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                MACD Line = EMA(12) - EMA(26)<br/>
                Signal Line = EMA(9) of MACD Line<br/>
                Histogram = MACD Line - Signal Line
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li>Signal line crossover: MACD crosses above/below signal line</li>
                <li>Zero line crossover: MACD crosses above/below zero</li>
                <li>Histogram analysis: Shows momentum changes</li>
                <li>Divergence: Price vs MACD direction differences</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Volatility Indicators Section */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            color: '#ff9800', 
            fontSize: '22px', 
            marginBottom: '20px',
            borderLeft: '4px solid #ff9800',
            paddingLeft: '15px'
          }}>
            📊 Volatility Indicators - Measuring Market Risk
          </h2>

          {/* Bollinger Bands */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a3a2a' : '#fff8f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #ff9800',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#ff9800', fontSize: '18px', marginBottom: '15px' }}>
              Bollinger Bands
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Created by John Bollinger in the 1980s. Based on the statistical concept 
                that 95% of price action occurs within 2 standard deviations.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>🧮 Calculation:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Middle Band = SMA(20)<br/>
                Upper Band = SMA(20) + (2 × Standard Deviation)<br/>
                Lower Band = SMA(20) - (2 × Standard Deviation)
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 Trading Concepts:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li><strong>Bollinger Squeeze:</strong> When bands contract (low volatility)</li>
                <li><strong>Band Walking:</strong> Price hugging upper/lower band (strong trend)</li>
                <li><strong>Double Top/Bottom:</strong> Price touches band twice</li>
                <li><strong>Mean Reversion:</strong> Price returns to middle band</li>
              </ul>
            </div>

            <div>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Example Strategy:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Entry: CLOSE &gt; BB_UPPER(20, 2.0) AND RSI(14) &gt; 50<br/>
                Exit: CLOSE &lt; BB_MIDDLE(20, 2.0)
              </div>
            </div>
          </div>

          {/* ATR */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a3a2a' : '#fff8f0',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #ff9800',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#ff9800', fontSize: '18px', marginBottom: '15px' }}>
              ATR - Average True Range
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Also created by J. Welles Wilder in 1978. Measures market volatility 
                without considering price direction.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How to Use:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li><strong>Stop Loss Placement:</strong> ATR × 2 from entry price</li>
                <li><strong>Position Sizing:</strong> Risk same ATR amount per trade</li>
                <li><strong>Breakout Confirmation:</strong> Volume × ATR expansion</li>
                <li><strong>Market Regime:</strong> High ATR = volatile, Low ATR = calm</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Advanced Indicators Section */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            color: '#9c27b0', 
            fontSize: '22px', 
            marginBottom: '20px',
            borderLeft: '4px solid #9c27b0',
            paddingLeft: '15px'
          }}>
            🚀 Advanced Indicators - Professional Tools
          </h2>

          {/* Supertrend */}
          <div style={{ 
            backgroundColor: isDarkTheme ? '#3a2a3a' : '#f8f0ff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #9c27b0',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#9c27b0', fontSize: '18px', marginBottom: '15px' }}>
              Supertrend - Dynamic Support/Resistance
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>📜 History:</strong>
              <p style={{ marginTop: '5px', color: currentTheme.textSecondary }}>
                Modern indicator combining ATR and moving averages. Popular among 
                Indian stock traders and gaining global recognition.
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: currentTheme.textPrimary }}>💡 How it Works:</strong>
              <ul style={{ marginTop: '8px', color: currentTheme.textSecondary, paddingLeft: '20px' }}>
                <li>Creates dynamic support/resistance levels</li>
                <li>Green line = Bullish trend (price above Supertrend)</li>
                <li>Red line = Bearish trend (price below Supertrend)</li>
                <li>Adapts to volatility using ATR</li>
              </ul>
            </div>

            <div>
              <strong style={{ color: currentTheme.textPrimary }}>📊 Example Strategy:</strong>
              <div style={{ 
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginTop: '8px',
                border: `1px solid ${currentTheme.border}`
              }}>
                Entry: SUPERTREND_BULL(10, 3.0) AND RSI(14) &gt; 50<br/>
                Exit: SUPERTREND_BEAR(10, 3.0)
              </div>
            </div>
          </div>
        </div>

        {/* Study Tips Section */}
        <div style={{ 
          backgroundColor: isDarkTheme ? '#2a2f3f' : '#e8f0fe',
          padding: '25px',
          borderRadius: '10px',
          border: '2px solid #4285f4'
        }}>
          <h3 style={{ color: '#4285f4', fontSize: '20px', marginBottom: '20px' }}>
            🎓 Study Tips for Students
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h4 style={{ color: currentTheme.textPrimary, marginBottom: '10px' }}>📝 Learning Strategy</h4>
              <ul style={{ color: currentTheme.textSecondary, paddingLeft: '20px', lineHeight: '1.6' }}>
                <li>Start with moving averages (SMA, EMA)</li>
                <li>Master RSI before other oscillators</li>
                <li>Practice on demo accounts first</li>
                <li>Study one indicator at a time</li>
              </ul>
            </div>
            
            <div>
              <h4 style={{ color: currentTheme.textPrimary, marginBottom: '10px' }}>⚖️ Risk Management</h4>
              <ul style={{ color: currentTheme.textSecondary, paddingLeft: '20px', lineHeight: '1.6' }}>
                <li>Use ATR for stop loss placement</li>
                <li>Never risk more than 2% per trade</li>
                <li>Combine indicators for confirmation</li>
                <li>Backtest before live trading</li>
              </ul>
            </div>
            
            <div>
              <h4 style={{ color: currentTheme.textPrimary, marginBottom: '10px' }}>🔍 Market Analysis</h4>
              <ul style={{ color: currentTheme.textSecondary, paddingLeft: '20px', lineHeight: '1.6' }}>
                <li>Multiple timeframe analysis</li>
                <li>Trend first, then signals</li>
                <li>Volume confirms price action</li>
                <li>Economic events affect indicators</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
